import { useState, useEffect } from "react";
import { Button, InlineAlert, LoadingSpinner } from "../../components/BaseComponents";
import SimpleSortTable from "../../components/BaseComponents/SimpleSortTable/SimpleSortTable";
import useApi from "../../hooks/useApi";
import Input from "../../components/BaseComponents/Forms/Input";
import CanonicalModal from "../../components/BaseComponents/CanonicalModal/CanonicalModal";
import { getApiErrorMessage, getCSRFToken } from "../../lib/utils";
import useDataSort from "../../hooks/useDataSort";
import usePagination from "../../hooks/usePagination";
import Pagination from "../../components/BaseComponents/Pagination/Pagination";
import useCleanup from "../../hooks/useCleanup";

function AdminUsers() {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    // Issue #884: loadData/onCreateUser previously had no catch at all, so a rejecting GET/POST
    // (including the now-rejecting READ_ONLY_ACCESS/ACCESS_AUDIT_UNAVAILABLE codes - #857) left
    // `loading` stuck true forever with no visible feedback, and a denied create silently
    // produced an unhandled rejection.
    const [loadError, setLoadError] = useState('');
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState('');

    const api = useApi();

    const [email, setEmail] = useState('');
    const [name, setName] = useState('');

    const [modalOpen, setModalOpen] = useState(false);
    const dataSort = useDataSort('email', 'asc');
    const pagination = usePagination();

    const onCloseModal = () => {
        setModalOpen(false);
        setCreateError('');
    }

    const loadData = useCleanup(() => {
        let cancelled = false;
        const run = async () => {
            setLoading(true);
            setLoadError('');
            try {
                const qs = new URLSearchParams({
                    ...pagination.params,
                    ...dataSort.params,
                })
                const result = await api.get('admin/adminUsers?' + qs)
                if(cancelled) {
                    return;
                }
                setData(result.data.users);
                pagination.readMeta(result.data.meta);
            } catch (err: any) {
                if(cancelled) {
                    return;
                }
                setLoadError(getApiErrorMessage(err, 'Failed to load admin users. Please try again.'));
            } finally {
                if(!cancelled) {
                    setLoading(false);
                }
            }
        }
        run()
        return () => {
            cancelled = true;
        }
    });

    useEffect(() => {
        loadData();
    }, [dataSort.params, pagination.params])

    const onCreateUser = async () => {
        setCreating(true);
        setCreateError('');
        try {
            await api.post('admin/adminUsers', {
                email,
                fullName: name,
                _csrf: await getCSRFToken(),
            })
            loadData();
        } catch (err: any) {
            // Denied write never treated as success: the modal stays open (matching the
            // pre-existing success behaviour of not auto-closing) so the admin can retry or
            // cancel without losing what they typed.
            setCreateError(getApiErrorMessage(err, 'Failed to create this admin user. Please try again.'));
        } finally {
            setCreating(false);
        }
    }

    if(loading) {
        <LoadingSpinner />
    }

    const columns = [
        {
            id: 'email',
            sortable: true,
            label: 'Email',
        },
        {
            id: 'fullName',
            sortable: true,
            label: 'Name',
        },
        {
            id: 'role',
            sortable: true,
            label: 'Role',
        },
    ]

    return (
        <div className="mt-9 text-white">
            <div className="flex">
                <div className="grow">Users</div>
                <Button type="Primary" text="Create" size="sm" onClick={() => setModalOpen(true)}/>
            </div>
            { loadError && (
                <InlineAlert type='error' className='mt-4'>
                    <p>{ loadError }</p>
                    <Button
                        type='Primary'
                        hasTrailingIcon={true}
                        icon='fa-solid fa-rotate-right'
                        text='Try again'
                        additionalClass='mt-4'
                        onClick={() => loadData()}
                    />
                </InlineAlert>
            )}
            <SimpleSortTable
                additionalClass='mt-4'
                data={data}
                columns={columns}
                isLoading={loading}
            />
            <Pagination
                pagination={pagination.pagination}
                onPageChange={pagination.onPageChange}
                isLoading={loading}
            />
            <CanonicalModal
                open={modalOpen}
                onClose={onCloseModal}
                size="small"
                title="Create admin user"
                actions={
                    <>
                        <Button
                            type="Primary"
                            text="Create admin user"
                            onClick={onCreateUser}
                            disabled={creating}
                            additionalClass="mr-5"
                        />
                        <Button type="Outlined" text="Cancel" onClick={onCloseModal}/>
                    </>
                }
            >
                    <div className="mt-8">
                        <Input
                            label="Name"
                            type="text"
                            name="name"
                            value={name}
                            onChange={e => setName(e.target.value)}
                        />
                    </div>
                    <div className="mt-4">
                        <Input
                            label="Email"
                            type="text"
                            name="email"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                        />
                    </div>
                    { createError && (
                        <InlineAlert type='error' className='mt-4'>{ createError }</InlineAlert>
                    )}
            </CanonicalModal>
        </div>
    )

}

export default AdminUsers;
